/**
 * 诚信公示 · 接口调用点（2026-08-26 批 D 新增）。
 *
 * 为什么另起一个文件：`contract.ts` / `citizen.ts` 是市民端全量契约与调用点，
 * 同期有另一路在改案件相关的部分。诚信公示这块从此走本文件，两边互不打扰。
 * 本文件的类型逐字对齐服务端 `credit-dto.ts`。
 */

import { apiGet, qs, CREDIT_TIMEOUT_MS } from './client';
import type { CreditSincerityItem } from './contract';

const P = '/api/citizen';

export type { CreditSincerityItem };

/** 一条奖惩（自律）记录。`content` 服务端已 URL 解码。 */
export interface CreditRecordItem {
  name: string;
  cdNumber: string;
  content: string;
  /** 形如「-70诚信分」「+5诚信分」 */
  score: string;
  time: string;
}

/** 经纪人详情（含自律记录）。 */
export interface CreditUserDetailFull {
  userId: number;
  name: string;
  cdNumber: string;
  orgName: string;
  sincerityState: string;
  score: string;
  memberType: string;
  star: string;
  expiryDate: string;
  /** `null` ＝ 没有这项数据；**不许折成 0** */
  complaintNum: number | null;
  rewardScore: number | null;
  punishScore: number | null;
  punishments: CreditRecordItem[];
  rewards: CreditRecordItem[];
  /** 非 null ＝ 奖惩记录这一路取失败。页面必须说「取不到」而不是显示成「没有」 */
  recordsError: string | null;
}

/** 黑名单经纪人详情。`user` 为 null ＝ 没能按编号匹配到在册档案。 */
export interface CreditBlackUserDetail {
  black: { name: string; cdNumber: string; reason: string; from: string; to: string } | null;
  user: CreditUserDetailFull | null;
  resolvedBy: 'true-name' | 'none';
  punishments: CreditRecordItem[];
  rewards: CreditRecordItem[];
  /** 非 null ＝ 自律记录这一路取失败。页面必须说「取不到」，**不许显示成「没有记录」** */
  recordsError: string | null;
  notes: string[];
}

export interface CreditOrgItem {
  orgId: number;
  orgName: string;
  sincerityState: string;
  score: string;
  orgType: string;
}

export interface CreditOrgSearchResult {
  keyword: string;
  /** 协会侧命中的**真实总条数** —— 页面上必须原样摆出来 */
  matchedTotal: number;
  shown: number;
  truncated: boolean;
  pageIndex: number;
  pageSize: number;
  items: CreditOrgItem[];
  byLabel: Record<string, number>;
  notes: string[];
}

export interface CreditOrgDetail {
  orgId: number;
  orgName: string;
  orgNum: string;
  memberType: string;
  sincerityState: string;
  score: string;
  orgType: string;
  black: { from: string; to: string } | null;
  punishments: CreditRecordItem[];
  rewards: CreditRecordItem[];
  recordsError: string | null;
  notes: string[];
}

/** 搜经纪人（姓名或编号；诚实版：命中多少、给了多少、有没有截断，三个数分开报）。 */
export interface CreditSearchResultV2 {
  query: string;
  /** 这次靠什么实现：姓名走 true-name，编号走 keyword，none ＝ 做不到 */
  supportedBy: 'true-name' | 'keyword' | 'none';
  matchedTotal: number;
  shown: number;
  truncated: boolean;
  total: number;
  items: CreditSincerityItem[];
  perState: Array<{ state: string; label: string; total: number; fetched: number; error: string | null }>;
  byLabel: Record<string, number>;
  notes: string[];
}

/** 检索经纪人。姓名与 CD 编号都支持；后端在 supportedBy 里标明这次走的哪条。 */
export function creditSearchV2(name: string, pageSize = 20): Promise<CreditSearchResultV2> {
  return apiGet<CreditSearchResultV2>(`${P}/credit/search${qs({ name, pageSize })}`, CREDIT_TIMEOUT_MS);
}

/** 经纪人详情（含自律记录）。 */
export function creditUserFull(userId: number): Promise<CreditUserDetailFull> {
  return apiGet<CreditUserDetailFull>(
    `${P}/credit/user/${encodeURIComponent(String(userId))}`,
    CREDIT_TIMEOUT_MS,
  );
}

/** 黑名单经纪人详情：黑名单条目 ＋ 反查到的在册档案 ＋ 自律记录。 */
export function creditBlackUser(cdNumber: string, name: string): Promise<CreditBlackUserDetail> {
  return apiGet<CreditBlackUserDetail>(
    `${P}/credit/black/user${qs({ cdNumber, name })}`,
    CREDIT_TIMEOUT_MS,
  );
}

/** 机构查询。 */
export function creditOrgSearch(keyword: string, pageIndex = 1, pageSize = 20): Promise<CreditOrgSearchResult> {
  return apiGet<CreditOrgSearchResult>(
    `${P}/credit/org/search${qs({ keyword, pageIndex, pageSize })}`,
    CREDIT_TIMEOUT_MS,
  );
}

/**
 * 机构详情。
 * `sincerityState` / `score` / `orgType` 由列表页带过来 —— 协会的机构详情接口不返回这三项，
 * 不带就是空，页面显示「—」。**不许在任何一端自己补一个值出来。**
 */
export function creditOrgDetail(q: {
  orgId?: number;
  name?: string;
  sincerityState?: string;
  score?: string;
  orgType?: string;
}): Promise<CreditOrgDetail> {
  return apiGet<CreditOrgDetail>(
    `${P}/credit/org/detail${qs({
      orgId: q.orgId && q.orgId > 0 ? q.orgId : undefined,
      name: q.name,
      sincerityState: q.sincerityState,
      score: q.score,
      orgType: q.orgType,
    })}`,
    CREDIT_TIMEOUT_MS,
  );
}
